import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Cpu, Edit3, Info, Zap, FileText } from 'lucide-react';
import { apiPath } from '../utils/appPaths';
import { useAuth } from '../contexts/AuthContext';
import { generateIcSvg } from '../utils/icSvgGenerator';
import {
  formatPinCurrent,
  formatPinVoltage
} from '../utils/compatibilityDisplay';

const PageContainer = styled.div`
  padding: 8px 0 clamp(24px, 3vw, 36px);
`;

const PageInner = styled.div`
  width: min(1200px, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
`;

const Header = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 24px;
  margin-top: 20px;
`;

const Title = styled.h1`
  font-size: clamp(1.25rem, 3vw, 2.5rem);
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  text-align: center;
`;

const SpecsColumn = styled.div`
  display: grid;
  gap: 18px;
  align-content: start;
`;

const Specs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const SpecItem = styled.div`
  background: white;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  text-align: center;
  min-width: 110px;
  flex: 0 0 auto;
`;

const SpecLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const SpecValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: 600;

  a {
    color: #2c9fa4;
    text-decoration: underline;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 350px;
  gap: clamp(24px, 3vw, 40px);
  align-items: start;

  @media (max-width: 1280px) {
    grid-template-columns: minmax(0, 1fr) 320px;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const DiagramSection = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 1100px;
  width: 100%;
  justify-self: center;
`;

const DiagramTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DiagramCanvas = styled.div`
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border-radius: 12px;
  min-height: clamp(480px, 72vh, 920px);
  padding: 0;
  overflow: hidden;
  border: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    min-height: clamp(360px, 58vh, 620px);
  }
`;

const SvgStage = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 24px;

  svg {
    display: block;
    max-width: 100%;
    height: auto;
  }

  svg .ic-pin-callout,
  svg .ic-pin-hitbox,
  svg .ic-pin-bubble,
  svg .ic-pin-label {
    cursor: pointer;
  }

  svg .ic-pin-label {
    pointer-events: none;
  }

  svg .ic-pin-callout:hover .ic-pin-marker,
  svg .ic-pin-callout.selected .ic-pin-marker {
    fill: #f59e0b;
    stroke: #ffffff;
    stroke-width: 3;
  }

  svg .ic-pin-callout:hover .ic-pin-bubble,
  svg .ic-pin-callout.selected .ic-pin-bubble {
    fill: #ecfeff;
    stroke: #2c9fa4;
    stroke-width: 2;
  }

  svg .ic-pin-callout.selected .ic-pin-bubble {
    fill: #fef3c7;
    stroke: #f59e0b;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$columns ? `repeat(${props.$columns}, minmax(0, 1fr))` : '1fr'};
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #64748b;
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  width: 100%;

  &:hover {
    background: #475569;
    color: white;
  }
`;

const EditButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #3b82f6;
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  width: 100%;

  &:hover {
    background: #2563eb;
    color: white;
  }
`;

const InfoPanel = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const PanelTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PinInfo = styled.div`
  text-align: center;
  color: #64748b;
  font-style: italic;
`;

const PinDetails = styled.div`
  .pin-name {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
  }

  .pin-number {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 16px;
  }

  .pin-functions {
    background: #f1f5f9;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    color: #475569;
  }

  .pin-specs {
    display: grid;
    gap: 8px;
  }

  .spec-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
  }

  .spec-label {
    color: #64748b;
  }

  .spec-value {
    color: #1e293b;
    font-weight: 500;
    text-align: right;
  }

  .pin-description {
    background: #f8fafc;
    padding: 12px;
    border-radius: 8px;
    margin-top: 16px;
    font-size: 14px;
    color: #475569;
    border-left: 3px solid #2c9fa4;
    line-height: 1.5;
  }
`;

const DetailsSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: clamp(20px, 2vw, 32px);
  margin-top: 32px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
`;

const Description = styled.div`
  font-size: 18px;
  color: #64748b;
  line-height: 1.6;
  text-align: left;

  p {
    margin: 8px 0;
  }

  a {
    color: #2c9fa4;
    text-decoration: underline;
  }

  strong {
    font-weight: 600;
    color: #1e293b;
  }
`;

const PinTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #f8fafc;
    color: #334155;
    font-weight: 700;
  }

  td {
    color: #475569;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #ef4444;
  font-size: 18px;
  margin: 40px 0;
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.9rem;
`;

const formatVoltageRange = (min, max) => {
  if (min || max) {
    return `${min || '?'}V - ${max || '?'}V`;
  }

  return 'N/A';
};

const normalizeMeaningfulText = (value, fallback = '') => {
  const normalizedValue = String(value ?? '').trim();

  if (!normalizedValue) {
    return fallback;
  }

  const lowerValue = normalizedValue.toLowerCase();
  if (lowerValue === 'undefined' || lowerValue === 'null') {
    return fallback;
  }

  return normalizedValue;
};

const humanizeSlug = (value, fallback = 'Untitled IC') => {
  const normalizedSlug = normalizeMeaningfulText(value);
  if (!normalizedSlug) {
    return fallback;
  }

  return normalizedSlug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const normalizeIcPin = (pin, index) => {
  const rawPinNumber = Number(pin?.pin_number ?? pin?.pinNumber ?? index + 1);
  const pinNumber = Number.isFinite(rawPinNumber) && rawPinNumber > 0
    ? rawPinNumber
    : index + 1;
  const pinName = String(pin?.pin_name ?? pin?.pinName ?? '').trim() || `PIN${pinNumber}`;

  return {
    ...pin,
    pin_number: pinNumber,
    pin_name: pinName,
    capability_tags: Array.isArray(pin?.capability_tags) ? pin.capability_tags : [],
  };
};

const normalizeIcRecord = (record) => {
  const normalizedSlug = normalizeMeaningfulText(record?.slug);
  const fallbackName = humanizeSlug(normalizedSlug, 'Untitled IC');
  const normalizedPackageVariant = normalizeMeaningfulText(record?.package_variant);
  const normalizedPackageType = normalizeMeaningfulText(record?.package_type, 'DIP');
  const normalizedPinCount = Number.parseInt(record?.pin_count, 10);

  return {
    ...record,
    name: normalizeMeaningfulText(record?.name, fallbackName),
    slug: normalizedSlug,
    manufacturer: normalizeMeaningfulText(record?.manufacturer),
    category: normalizeMeaningfulText(record?.category),
    description: normalizeMeaningfulText(record?.description),
    datasheet_url: normalizeMeaningfulText(record?.datasheet_url),
    package_variant: normalizedPackageVariant,
    package_type: normalizedPackageType,
    marker_style: normalizeMeaningfulText(record?.marker_style, 'notch-dot'),
    label_mode: normalizeMeaningfulText(record?.label_mode, 'name'),
    pin_count: Number.isInteger(normalizedPinCount) && normalizedPinCount > 0 ? normalizedPinCount : null,
    pins: Array.isArray(record?.pins) ? record.pins : [],
  };
};

const getPinKey = (pin) => String(pin?.pin_number ?? pin?.id ?? '');

const ICPinoutDetail = () => {
  const { slug } = useParams();
  const [ic, setIc] = useState(null);
  const [selectedPin, setSelectedPin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { isAuthenticated } = useAuth();
  const isAdmin = isAuthenticated || Boolean(localStorage.getItem('adminToken'));

  useEffect(() => {
    const fetchIc = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(apiPath(`/ic-pinouts/${slug}`));
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to load IC pinout.');
        }

        setIc(normalizeIcRecord(data.data));
        setSelectedPin(null);
      } catch (error) {
        setError(error.message || 'Failed to load IC pinout.');
      } finally {
        setLoading(false);
      }
    };

    fetchIc();
  }, [slug]);

  const pins = useMemo(
    () => (Array.isArray(ic?.pins) ? ic.pins : []).map(normalizeIcPin),
    [ic?.pins]
  );
  const findPinByNumber = (pinNumber) => (
    pins.find((pin) => String(pin.pin_number) === String(pinNumber)) || null
  );
  const renderedSvgContent = useMemo(() => {
    if (!ic) {
      return '';
    }

    // Rebuild from live pin metadata so stale stored SVGs don't render "undefined" labels.
    const generatedSvg = generateIcSvg({
      name: ic.name || 'IC',
      packageType: ic.package_type || 'DIP',
      pinCount: ic.pin_count || pins.length || 8,
      markerStyle: ic.marker_style || 'notch-dot',
      labelMode: ic.label_mode || 'name',
      pins,
    });

    return generatedSvg || ic.svg_content || '';
  }, [ic, pins]);

  if (loading) {
    return (
      <PageContainer>
        <PageInner>
          <LoadingSpinner>
            <div className="loading-spinner"></div>
          </LoadingSpinner>
        </PageInner>
      </PageContainer>
    );
  }

  if (error || !ic) {
    return (
      <PageContainer>
        <PageInner>
          <ErrorMessage>{error || 'IC pinout not found'}</ErrorMessage>
        </PageInner>
      </PageContainer>
    );
  }

  const handleSvgClick = (event) => {
    const target = event.target.closest?.('[data-pin-number]');

    if (!target) {
        return;
    }

    const pinNumber = target.getAttribute('data-pin-number');
    const pin = findPinByNumber(pinNumber);

    if (!pin) {
        return;
    }

    setSelectedPin(pin);

    const svgRoot = event.currentTarget.querySelector('svg');

    if (svgRoot) {
        svgRoot
        .querySelectorAll('.ic-pin-callout.selected')
        .forEach((element) => element.classList.remove('selected'));

        svgRoot
        .querySelectorAll(`.ic-pin-callout[data-pin-number="${pinNumber}"]`)
        .forEach((element) => element.classList.add('selected'));
            }
    };

  return (
    <PageContainer>
      <PageInner>
        <Header>
          <Title>
            <Cpu size={32} />
            {ic.name} Pinout
          </Title>

          <SpecsColumn>
            <Specs>
              <SpecItem>
                <SpecLabel>Package</SpecLabel>
                <SpecValue>{ic.package_variant || `${ic.package_type}-${ic.pin_count}`}</SpecValue>
              </SpecItem>

              <SpecItem>
                <SpecLabel>Pins</SpecLabel>
                <SpecValue>{ic.pin_count}</SpecValue>
              </SpecItem>

              <SpecItem>
                <SpecLabel>Category</SpecLabel>
                <SpecValue>{ic.category || 'N/A'}</SpecValue>
              </SpecItem>

              <SpecItem>
                <SpecLabel>Supply</SpecLabel>
                <SpecValue>{formatVoltageRange(ic.supply_voltage_min_v, ic.supply_voltage_max_v)}</SpecValue>
              </SpecItem>

              <SpecItem>
                <SpecLabel>Logic</SpecLabel>
                <SpecValue>{formatVoltageRange(ic.logic_voltage_min_v, ic.logic_voltage_max_v)}</SpecValue>
              </SpecItem>

              {ic.datasheet_url && (
                <SpecItem>
                  <SpecLabel>Datasheet</SpecLabel>
                  <SpecValue>
                    <a href={ic.datasheet_url} target="_blank" rel="noopener noreferrer">
                      Link
                    </a>
                  </SpecValue>
                </SpecItem>
              )}
            </Specs>
          </SpecsColumn>
        </Header>

        <ContentGrid>
          <DiagramSection>
            <DiagramCanvas>
                <SvgStage
                    onClick={handleSvgClick}
                    dangerouslySetInnerHTML={{ __html: renderedSvgContent }}
                />
            </DiagramCanvas>
          </DiagramSection>

          <Sidebar>
            <ButtonContainer $columns={isAdmin ? 2 : 1}>
              <BackButton to="/ic-pinouts">
                <ArrowLeft size={16} />
                Back
              </BackButton>

              {isAdmin && (
                <EditButton to={`/admin?tab=ic-pinouts`}>
                  <Edit3 size={16} />
                  Edit
                </EditButton>
              )}
            </ButtonContainer>

            <InfoPanel>
              <PanelTitle>
                <Info size={20} />
                Pin Information
              </PanelTitle>

              <AnimatePresence mode="wait">
                {selectedPin ? (
                  <motion.div
                    key={getPinKey(selectedPin)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <PinDetails>
                      <div className="pin-name">{selectedPin.pin_name}</div>
                      <div className="pin-number">Pin {selectedPin.pin_number}</div>

                      {selectedPin.functions && (
                        <div className="pin-functions">
                          <strong>Primary Function:</strong><br />
                          {selectedPin.functions}
                        </div>
                      )}

                      {selectedPin.alternate_functions && (
                        <div className="pin-functions">
                          <strong>Alternate Functions:</strong><br />
                          {selectedPin.alternate_functions}
                        </div>
                      )}

                      <div className="pin-specs">
                        {formatPinVoltage(selectedPin) && (
                          <div className="spec-row">
                            <span className="spec-label">Voltage:</span>
                            <span className="spec-value">{formatPinVoltage(selectedPin)}</span>
                          </div>
                        )}

                        {formatPinCurrent(selectedPin) && (
                          <div className="spec-row">
                            <span className="spec-label">Current:</span>
                            <span className="spec-value">{formatPinCurrent(selectedPin)}</span>
                          </div>
                        )}

                        {selectedPin.pin_role && (
                          <div className="spec-row">
                            <span className="spec-label">Role:</span>
                            <span className="spec-value">{selectedPin.pin_role}</span>
                          </div>
                        )}

                        {Array.isArray(selectedPin.capability_tags) && selectedPin.capability_tags.length > 0 && (
                          <div className="spec-row">
                            <span className="spec-label">Tags:</span>
                            <span className="spec-value">{selectedPin.capability_tags.join(', ')}</span>
                          </div>
                        )}
                      </div>

                      {selectedPin.description && (
                        <div className="pin-description">
                          <strong>Description:</strong><br />
                          <div dangerouslySetInnerHTML={{ __html: selectedPin.description }} />
                        </div>
                      )}
                    </PinDetails>
                  </motion.div>
                ) : (
                  <PinInfo>
                    Click on a pin to view detailed information
                  </PinInfo>
                )}
              </AnimatePresence>
            </InfoPanel>

            <InfoPanel>
              <PanelTitle>
                <FileText size={20} />
                IC Details
              </PanelTitle>

              <PinDetails>
                <div className="pin-specs">
                  <div className="spec-row">
                    <span className="spec-label">Manufacturer:</span>
                    <span className="spec-value">{ic.manufacturer || 'N/A'}</span>
                  </div>

                  <div className="spec-row">
                    <span className="spec-label">Package:</span>
                    <span className="spec-value">{ic.package_variant || 'N/A'}</span>
                  </div>

                  <div className="spec-row">
                    <span className="spec-label">Marker:</span>
                    <span className="spec-value">{ic.marker_style || 'N/A'}</span>
                  </div>
                </div>
              </PinDetails>
            </InfoPanel>
          </Sidebar>
        </ContentGrid>

        <DetailsSection>
          <DiagramTitle>
            <Info size={24} />
            About {ic.name}
          </DiagramTitle>
          <Description dangerouslySetInnerHTML={{ __html: ic.description || `${ic.name} ${ic.package_variant || ''} pinout reference.` }} />
        </DetailsSection>

        <DetailsSection>
          <DiagramTitle>
            <Zap size={24} />
            {ic.name} Pin Functions
          </DiagramTitle>

          <PinTable>
            <thead>
              <tr>
                <th>Pin</th>
                <th>Name</th>
                <th>Role</th>
                <th>Function</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {pins.map((pin) => (
                <tr key={getPinKey(pin)}>
                  <td>{pin.pin_number}</td>
                  <td><strong>{pin.pin_name}</strong></td>
                  <td>{pin.pin_role || '-'}</td>
                  <td>{pin.functions || '-'}</td>
                  <td>{pin.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </PinTable>
        </DetailsSection>
      </PageInner>

      <Copyright>
        <strong>&copy; 2026 micropinouts.com</strong><br />
        All rights reserved.<br />
        Educational use and community sharing encouraged under applicable open-source licenses.
      </Copyright>
    </PageContainer>
  );
};

export default ICPinoutDetail;
